import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError, request } from '../lib/api.js';
import { useCan } from '../lib/session.js';

/**
 * Editing a quote or an invoice while it is still a draft.
 *
 * One component for both because they are the same act on the same shape — a note, a date, and
 * a list of priced lines — and the only differences are which date and which noun. Two
 * near-identical editors would drift, and the one that drifted would be the money one.
 *
 * **It disappears the moment the document is issued.** Not disabled: gone. A form that is
 * visible but refuses every keystroke invites somebody to look for the way around it, and there
 * isn't one — a sent quote and an issued invoice are copies somebody else is holding. The panel
 * is replaced by a line saying what to do instead, which is the same thing the API's 409 says.
 *
 * **Totals are not computed here.** Each line shows quantity × price, which is arithmetic
 * anybody can check, and the taxes and document totals come back from the server after saving.
 * A second tax implementation in the browser is a second answer to a question that has one
 * correct answer, and the wrong one would be the one the coordinator reads.
 */

export type EditableItem = {
  name: string;
  description: string | null;
  quantity: string;
  unitPrice: string;
  taxable?: boolean;
  optional?: boolean;
  selected?: boolean;
  unitOfMeasure?: string | null;
  productId?: string | null;
};

type Row = {
  name: string;
  quantity: string;
  unitPrice: string;
  taxable: boolean;
  optional: boolean;
  selected: boolean;
  description: string | null;
  unitOfMeasure: string | null;
  productId: string | null;
};

const rowOf = (item: EditableItem): Row => ({
  name: item.name,
  quantity: item.quantity,
  unitPrice: item.unitPrice,
  taxable: item.taxable ?? true,
  optional: item.optional ?? false,
  selected: item.selected ?? true,
  description: item.description,
  unitOfMeasure: item.unitOfMeasure ?? null,
  productId: item.productId ?? null,
});

const BLANK: Row = {
  name: '',
  quantity: '1',
  unitPrice: '0',
  taxable: true,
  optional: false,
  selected: true,
  description: null,
  unitOfMeasure: null,
  productId: null,
};

/** Pre-tax line amount, shown as a check on the arithmetic rather than as a total. */
function lineTotal(row: Row): number {
  const quantity = Number.parseFloat(row.quantity);
  const price = Number.parseFloat(row.unitPrice);
  if (!Number.isFinite(quantity) || !Number.isFinite(price)) return 0;
  return Math.round(quantity * price * 100) / 100;
}

export function DraftDocumentEditor({
  kind,
  id,
  status,
  note,
  date,
  items,
  locale,
}: {
  kind: 'quote' | 'invoice';
  id: string;
  status: string;
  note: string | null;
  /** `expiresOn` for a quote, `dueOn` for an invoice. */
  date: string | null;
  items: EditableItem[];
  locale: string;
}) {
  const { t: translate } = useTranslation();
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;
  const client = useQueryClient();
  const can = useCan();

  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Row[]>(() => items.map(rowOf));
  const [noteDraft, setNoteDraft] = useState(note ?? '');
  const [dateDraft, setDateDraft] = useState(date ? date.slice(0, 10) : '');
  const [error, setError] = useState<string | null>(null);

  const isDraft = status === 'draft';
  const mayEdit = can('billing.write');

  const save = useMutation({
    mutationFn: () => {
      const dateKey = kind === 'quote' ? 'expiresOn' : 'dueOn';
      return request(`/${kind}s/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          note: noteDraft.trim() || null,
          // A date control gives a bare day; the API wants an instant.
          [dateKey]: dateDraft ? new Date(`${dateDraft}T00:00:00`).toISOString() : null,
          items: rows
            .filter((row) => row.name.trim())
            .map((row) => ({
              name: row.name.trim(),
              description: row.description ?? undefined,
              quantity: Number.parseFloat(row.quantity) || 1,
              unitPrice: Number.parseFloat(row.unitPrice) || 0,
              taxable: row.taxable,
              // Only a quote has optional lines; sending them on an invoice would be a field
              // the endpoint does not accept.
              ...(kind === 'quote' ? { optional: row.optional, selected: row.selected } : {}),
              unitOfMeasure: row.unitOfMeasure ?? undefined,
              productId: row.productId ?? undefined,
            })),
        }),
      });
    },
    onSuccess: () => {
      setOpen(false);
      void client.invalidateQueries({ queryKey: [kind, id] });
      void client.invalidateQueries({ queryKey: [`${kind}s`] });
      // A document's total belongs to its job's rollup and the customer's balance.
      void client.invalidateQueries({ queryKey: ['job'] });
      void client.invalidateQueries({ queryKey: ['customer'] });
    },
    onError: (err) => setError(err instanceof ApiError && err.message ? err.message : t('draft.failed')),
  });

  if (!mayEdit) return null;

  if (!isDraft) {
    return (
      <p className="draft__closed">
        {kind === 'quote' ? t('draft.quoteIssued') : t('draft.invoiceIssued')}
      </p>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        className="btn btn--primary"
        onClick={() => {
          setError(null);
          setRows(items.map(rowOf));
          setNoteDraft(note ?? '');
          setDateDraft(date ? date.slice(0, 10) : '');
          setOpen(true);
        }}
      >
        {t('draft.edit')}
      </button>
    );
  }

  const named = rows.some((row) => row.name.trim());
  const busy = save.isPending;
  const preTax = rows
    .filter((row) => row.name.trim() && (!row.optional || row.selected))
    .reduce((sum, row) => sum + lineTotal(row), 0);

  return (
    <div className="draft">
      {error && (
        <p className="recform__error" role="alert">
          {error}
        </p>
      )}

      <div className="draft__lines">
        {rows.map((row, index) => (
          <div className="draft__line" key={index}>
            <input
              className="recform__input"
              aria-label={t('draft.lineName')}
              placeholder={t('draft.lineName')}
              value={row.name}
              disabled={busy}
              onChange={(e) => setRows(rows.map((r, i) => (i === index ? { ...r, name: e.target.value } : r)))}
            />
            <input
              className="recform__input tnum"
              aria-label={t('draft.quantity')}
              inputMode="decimal"
              value={row.quantity}
              disabled={busy}
              onChange={(e) => setRows(rows.map((r, i) => (i === index ? { ...r, quantity: e.target.value } : r)))}
            />
            <input
              className="recform__input tnum"
              aria-label={t('draft.unitPrice')}
              inputMode="decimal"
              value={row.unitPrice}
              disabled={busy}
              onChange={(e) => setRows(rows.map((r, i) => (i === index ? { ...r, unitPrice: e.target.value } : r)))}
            />
            <span className="draft__amount tnum">{lineTotal(row).toFixed(2)}</span>
            <label className="draft__flag">
              <input
                type="checkbox"
                checked={row.taxable}
                disabled={busy}
                onChange={(e) => setRows(rows.map((r, i) => (i === index ? { ...r, taxable: e.target.checked } : r)))}
              />
              {t('draft.taxable')}
            </label>
            {kind === 'quote' && (
              <label className="draft__flag">
                <input
                  type="checkbox"
                  checked={row.optional}
                  disabled={busy}
                  onChange={(e) =>
                    setRows(rows.map((r, i) => (i === index ? { ...r, optional: e.target.checked } : r)))
                  }
                />
                {t('draft.optional')}
              </label>
            )}
            <button
              type="button"
              className="recform__remove"
              aria-label={t('draft.removeLine')}
              disabled={busy || rows.length <= 1}
              onClick={() => setRows(rows.filter((_, i) => i !== index))}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <path d="M2.5 6h7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        ))}

        <button
          type="button"
          className="recform__add"
          disabled={busy || rows.length >= 60}
          onClick={() => setRows([...rows, { ...BLANK }])}
        >
          {t('draft.addLine')}
        </button>
      </div>

      {/* Labelled as pre-tax on purpose: the taxes and the real total come from the server. */}
      <p className="draft__subtotal">
        {t('draft.preTax')} <span className="tnum">{preTax.toFixed(2)}</span>
      </p>

      <div className="recform__pair">
        <div className="recform__field">
          <label className="recform__label">
            {kind === 'quote' ? t('draft.expiresOn') : t('draft.dueOn')}{' '}
            <span className="recform__optional">{t('common.optional')}</span>
          </label>
          <input
            type="date"
            className="recform__input"
            value={dateDraft}
            disabled={busy}
            onChange={(e) => setDateDraft(e.target.value)}
          />
        </div>
        <div className="recform__field">
          <label className="recform__label">
            {t('draft.note')} <span className="recform__optional">{t('common.optional')}</span>
          </label>
          <input
            className="recform__input"
            value={noteDraft}
            disabled={busy}
            onChange={(e) => setNoteDraft(e.target.value)}
            placeholder={t('draft.notePlaceholder')}
          />
        </div>
      </div>

      <div className="recform__row">
        <button type="button" className="btn btn--primary" disabled={busy || !named} onClick={() => save.mutate()}>
          {busy ? t('customer.form.saving') : t('common.save')}
        </button>
        <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </button>
        <span className="recform__spacer" />
      </div>
    </div>
  );
}
